import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import {OligoAwareFolderItemType, TagSchema, TagSchemaSummary} from 'constants/RegistryConstants';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaIdAndTypeInfo, oligoAwareFolderItemTypeMatchesTagSchema} from 'tag-schema/util/TagSchemaUtil';

import {ArchiveRecordGraphQL} from '../../graphql-fragments/app-core/platform/archivable/__generated/ArchiveRecordGraphQL';

export interface TagSchemaBasicFilters {
  includeArchived?: boolean;
  oligoAwareFolderItemTypes?: Array<OligoAwareFolderItemType> | null;
  alwaysIncludeId?: string;
}

export interface TagSchemaFilters extends TagSchemaBasicFilters {
  registryId?: string | null;
  requireTranslationLink?: boolean;
  requireTranscriptionLink?: boolean;
  requiredPermissions?: Array<keyof Permissions>;
  customFilter?: (tagSchema: TagSchema) => boolean;
}

export type BasicFilterableTagSchema = SchemaIdAndTypeInfo & Pick<TagSchemaSummary, 'archiveRecord' | 'name'>;

/** Function for filtering tag schemas that does not depend on the full REST representation. */
export function tagSchemaMatchesBasicFilters(
  ts: BasicFilterableTagSchema,
  filters: TagSchemaBasicFilters
): boolean {
  if (filters.includeArchived == null) {
    filters.includeArchived = false;
  }

  const oligoAwareFolderItemTypeFilter =
    filters.oligoAwareFolderItemTypes == null ||
    filters.oligoAwareFolderItemTypes.some((oligoAwareType) =>
      oligoAwareFolderItemTypeMatchesTagSchema(oligoAwareType, ts)
    );

  return oligoAwareFolderItemTypeFilter && (!isArchived(ts) || filters.includeArchived);
}

export default function filterTagSchemas(
  tagSchemas: Array<TagSchema>,
  filters: TagSchemaFilters = {}
): Array<TagSchema> {
  return tagSchemas.filter((ts) => {
    if (filters.alwaysIncludeId && filters.alwaysIncludeId === ts.id) {
      return true;
    }

    const registryIdFilter = filters.registryId == null || ts.registryId === filters.registryId;
    const transcriptionLinkFilter =
      !filters.requireTranscriptionLink ||
      ts.fields.some((f) => f.fieldType === FieldType.TRANSCRIPTION_LINK);
    const translationLinkFilter =
      !filters.requireTranslationLink || ts.fields.some((f) => f.fieldType === FieldType.TRANSLATION_LINK);
    const permissionsFilter =
      !filters.requiredPermissions || filters.requiredPermissions.every((perm) => ts.permissions[perm]);
    const customFilter = !filters.customFilter || filters.customFilter(ts);
    return (
      tagSchemaMatchesBasicFilters(ts, filters) &&
      registryIdFilter &&
      transcriptionLinkFilter &&
      translationLinkFilter &&
      permissionsFilter &&
      customFilter
    );
  });
}

export interface FilterableTagSchemaGraphQL extends SchemaIdAndTypeInfo {
  id: string;
  name: string;
  registry: {
    id: string;
  };
  fields?: Array<SchemaField>;
  fieldsJson?: Array<SchemaField>;
  permissions: {
    objectsCreatable: boolean | null;
    objectsRegisterable: boolean | null;
  };
  archiveRecord: ArchiveRecordGraphQL | null;
}

export interface TagSchemaFiltersGraphQL extends TagSchemaBasicFilters {
  registryId?: string | null;
  requireTranslationLink?: boolean;
  requireTranscriptionLink?: boolean;
  requiredPermissions?: Array<keyof Permissions>;
  customFilter?: (tagSchema: FilterableTagSchemaGraphQL) => boolean;
}

export function filterTagSchemasGraphQL<T extends FilterableTagSchemaGraphQL>(
  tagSchemas: Array<T>,
  filters: TagSchemaFiltersGraphQL = {}
): Array<T> {
  return tagSchemas.filter((ts) => {
    if (filters.alwaysIncludeId && filters.alwaysIncludeId === ts.id) {
      return true;
    }

    const registryIdFilter = filters.registryId == null || ts.registry.id === filters.registryId;

    let transcriptionLinkFilter = true;
    let translationLinkFilter = true;
    if (filters.requireTranscriptionLink || filters.requireTranslationLink) {
      assert(
        ts.hasOwnProperty('fields') || ts.hasOwnProperty('fieldsJson'),
        'Tag schema must have fields to filter by transcription or translation link'
      );

      const fields = ts.hasOwnProperty('fields')
        ? (ts.fields as Array<SchemaField>)
        : (ts.fieldsJson as Array<SchemaField>);

      transcriptionLinkFilter =
        !filters.requireTranscriptionLink ||
        fields.some((f: SchemaField) => f.fieldType === FieldType.TRANSCRIPTION_LINK);
      translationLinkFilter =
        !filters.requireTranslationLink ||
        fields.some((f: SchemaField) => f.fieldType === FieldType.TRANSLATION_LINK);
    }

    const permissionsFilter =
      !filters.requiredPermissions ||
      filters.requiredPermissions.every(
        (perm) => ts.permissions[perm as keyof FilterableTagSchemaGraphQL['permissions']]
      );

    const customFilter = !filters.customFilter || filters.customFilter(ts);

    return (
      tagSchemaMatchesBasicFilters(ts, filters) &&
      registryIdFilter &&
      transcriptionLinkFilter &&
      translationLinkFilter &&
      permissionsFilter &&
      customFilter
    );
  });
}
